import React from 'react';
import { Zap, Flame, Leaf, Thermometer, TrendingUp } from 'lucide-react';

interface EnergyMetricsProps {
  data: any;
}

const EnergyMetrics: React.FC<EnergyMetricsProps> = ({ data }) => {
  if (!data || !data.summary) {
    return (
      <div className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Energy & Environmental Metrics
        </h3>
        <p className="text-gray-500 dark:text-gray-400 text-sm">No energy data available</p>
      </div>
    );
  }

  const { summary } = data;

  const metrics = [
    {
      icon: Zap,
      label: 'Total Power',
      value: summary.total_power_w,
      unit: 'W',
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20'
    },
    {
      icon: Flame,
      label: 'Heat Output',
      value: summary.total_heat_btu,
      unit: 'BTU',
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20'
    },
    {
      icon: Leaf,
      label: 'Carbon Footprint',
      value: summary.total_carbon_kg,
      unit: 'Kg CO₂',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20'
    },
    {
      icon: Thermometer,
      label: 'Avg Temperature',
      value: summary.avg_ambient_temp_c,
      unit: '°C',
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20'
    }
  ];

  const efficiencyMetrics = [
    {
      icon: TrendingUp,
      label: 'Power Efficiency',
      value: summary.avg_power_efficiency_wtb,
      unit: 'W/TB',
      description: 'Average watts per terabyte'
    },
    {
      icon: Leaf,
      label: 'Carbon Efficiency',
      value: summary.avg_carbon_efficiency_kgtb,
      unit: 'Kg/TB',
      description: 'Average carbon per terabyte'
    }
  ];

  return (
    <div className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Energy & Environmental Metrics
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {summary.total_systems} systems monitored
        </span>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metrics.map((metric, idx) => {
          const Icon = metric.icon;
          return (
            <div
              key={idx}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <div className={`p-2 rounded-lg ${metric.bgColor} mb-2`}>
                <Icon className={`w-5 h-5 ${metric.color}`} />
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {metric.value !== null && metric.value !== undefined
                    ? metric.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : 'N/A'}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {metric.unit}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {metric.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Efficiency Metrics */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Efficiency Metrics
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {efficiencyMetrics.map((metric, idx) => {
            const Icon = metric.icon;
            return (
              <div
                key={idx}
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50"
              >
                <Icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {metric.value !== null && metric.value !== undefined
                        ? metric.value.toFixed(metric.unit === 'Kg/TB' ? 4 : 2)
                        : 'N/A'}
                    </span>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {metric.unit}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    {metric.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EnergyMetrics;